<template>
  <div class="canvas-editor">
    <div
      class="editor-holder"
      :style="holderStyle"
    >
      <div
        class="editor-ratio"
        :style="ratioStyle"
      >
        <div
          class="editor-video"
          :style="videoStyle"
        >
          <div :style="videoBoxStyle">
            <video
              ref="videoDom"
              crossorigin="anonymous"
              :src="videoUrl"
            ></video>
          </div>
        </div>
        <div
          class="editor-handle"
          :style="handleStyle"
          @mousewheel="mousewheel($event)"
        >
          <div class="handle-view-box">
            <span class="handle-line-h"></span>
            <span class="handle-line-v"></span>
          </div>
          <div
            v-dragWhole
            class="handle-drag-box"
            data-action="move"
          >
            <span
              v-for="(v, i) in drags"
              :key="i"
              :class="v.c"
              :data-action="v.a"
            ></span>
          </div>
        </div>
        <div
          class="color-pick-btn"
          @click="selectColor"
        >
          <iconfont
            class="normal"
            name="#icon15-51tianse"
            font-size="24px"
            color="#a7aab6"
          ></iconfont>
          <iconfont
            class="hover"
            name="#icon15-51tianse"
            font-size="24px"
            color="#2f58fe"
          ></iconfont>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'CanvasEditor',
  props: {
    videoUrl: {
      type: String,
      default: ''
    },
    volume: {
      type: Number,
      default: 1
    },
    rotate: {
      type: Number,
      default: 0
    },
    ratio: {
      type: Number,
      required: true,
      default: 0
    },
    matrix: {
      type: Object,
      required: true,
      default: function() {
        return {
          w: 0,
          h: 0,
          x: 0,
          y: 0
        };
      }
    }
  },
  data() {
    return {
      playing: false,
      times: 0,
      canPlay: false,
      duration: 0,
      holder: {
        w: 0,
        h: 0,
        x: 0,
        y: 0
      },
      ratioN: 1,
      ratioM: {
        w: 0,
        h: 0,
        x: 0,
        y: 0
      },
      initSize: {
        w: 0,
        h: 0,
        r: 1,
        s: 1
      },
      videoSize: {
        w: 0,
        h: 0,
        x: 0,
        y: 0
      },
      handleSize: {
        w: 0,
        h: 0,
        x: 0,
        y: 0
      },
      drags: [
        { c: 'line-e', a: 'e' },
        { c: 'line-n', a: 'n' },
        { c: 'line-w', a: 'w' },
        { c: 'line-s', a: 's' },
        { c: 'point-ne canvas-point', a: 'ne' },
        { c: 'point-nw canvas-point', a: 'nw' },
        { c: 'point-sw canvas-point', a: 'sw' },
        { c: 'point-se canvas-point', a: 'se' }
      ],
      scale: 1,
      action: '',
      mpos: null,
      bgColor: '#000000',
      wheelScale: 1
    };
  },
  computed: {
    holderStyle() {
      const style = {};
      style.width = this.holder.w + 'px';
      style.height = this.holder.h + 'px';
      style.top = this.holder.y + 'px';
      style.left = this.holder.x + 'px';
      return style;
    },
    ratioStyle() {
      const style = {};
      style.width = this.ratioM.w + 'px';
      style.height = this.ratioM.h + 'px';
      style.top = this.ratioM.y + 'px';
      style.left = this.ratioM.x + 'px';
      style.backgroundColor = this.bgColor;
      return style;
    },
    videoStyle() {
      const style = {};
      style.width = this.videoSize.w + 'px';
      style.height = this.videoSize.h + 'px';
      style.top = this.videoSize.y + 'px';
      style.left = this.videoSize.x + 'px';
      return style;
    },
    videoBoxStyle() {
      const style = {};
      let w = this.videoSize.w,
        h = this.videoSize.h,
        t = 0,
        l = 0;
      if (this.rotate === 90 || this.rotate === 270) {
        w = this.videoSize.h;
        h = this.videoSize.w;
        t = (w - h) / 2;
        l = (h - w) / 2;
      }
      style.width = w + 'px';
      style.height = h + 'px';
      style.top = t + 'px';
      style.left = l + 'px';
      style.transform = `rotate(${this.rotate}deg)`;
      return style;
    },
    handleStyle() {
      const style = {};
      style.width = this.handleSize.w + 'px';
      style.height = this.handleSize.h + 'px';
      style.top = this.handleSize.y + 'px';
      style.left = this.handleSize.x + 'px';
      style.boxShadow = '0 0 0 1999px ' + this.bgColor;
      return style;
    }
  },
  watch: {
    volume() {
      this.$refs.videoDom.volume = this.volume;
    },
    matrix: {
      deep: true,
      handler() {
        let old = this.ratioM.w;
        this.calcData();
        let zoom = this.ratioM.w / old;
        this.resizeMatrix(this.videoSize, zoom);
        this.resizeMatrix(this.handleSize, zoom);
      }
    },
    rotate() {
      [this.initSize.w, this.initSize.h] = [this.initSize.h, this.initSize.w];
      this.initSize.r = this.initSize.w / this.initSize.h;
      Object.assign(this.videoSize, this.getVideoMatrix(this.ratioM));
      Object.assign(this.handleSize, this.videoSize);
      this.initSize.s = this.videoSize.w / this.ratioM.w;
    },
    ratio() {
      this.calcData();
      Object.assign(this.videoSize, this.getVideoMatrix(this.ratioM));
      Object.assign(this.handleSize, this.videoSize);
      this.initSize.s = this.videoSize.w / this.ratioM.w;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.videoDom.addEventListener('canplay', this.canPlayFunc);
      this.$refs.videoDom.addEventListener('timeupdate', this.timeupdate);
      this.$refs.videoDom.addEventListener('durationchange', this.durationchange);
    });
  },
  beforeDestroy() {
    this.$refs.videoDom.removeEventListener('canplay', this.canPlayFunc);
    this.$refs.videoDom.removeEventListener('timeupdate', this.timeupdate);
    this.$refs.videoDom.removeEventListener('durationchange', this.durationchange);
    this.$refs.videoDom = null;
  },
  methods: {
    canPlayFunc() {
      if (!this.canPlay) {
        this.canPlay = true;
        let w = this.$refs.videoDom.videoWidth;
        let h = this.$refs.videoDom.videoHeight;
        if (this.rotate === 90 || this.rotate === 270) {
          this.initSize.w = h;
          this.initSize.h = w;
        } else {
          this.initSize.w = w;
          this.initSize.h = h;
        }

        this.initSize.r = this.initSize.w / this.initSize.h;
        this.calcData();
        Object.assign(this.videoSize, this.getVideoMatrix(this.ratioM));
        Object.assign(this.handleSize, this.videoSize);
        this.initSize.s = this.videoSize.w / this.ratioM.w;
        this.$emit('canPlay', this.duration);
      }
    },
    durationchange() {
      this.canPlay = false;
      this.duration = this.$refs.videoDom.duration;
    },
    videoPlay(data) {
      if (typeof data.time === 'number') {
        this.currentTime(data.time);
      }
      if (data.play) {
        this.play();
      } else {
        this.pause();
      }
    },
    play() {
      if (!this.canPlay) return;
      this.$refs.videoDom.play();
      this.playing = true;
    },
    pause() {
      if (!this.canPlay) return;
      this.$refs.videoDom.pause();
      this.playing = false;
    },
    currentTime(time) {
      if (!this.canPlay) return;
      this.$refs.videoDom.currentTime = time;
    },
    timeupdate() {
      if (!this.playing) return;
      let ct = this.$refs.videoDom.currentTime;
      if (ct >= this.duration) {
        this.$emit('ended');
      }
      this.$emit('timeupdate', ct);
    },
    calcData() {
      Object.assign(this.holder, this.getHolder(this.matrix));
      Object.assign(this.ratioM, this.getRatioMatrix(this.holder));
    },
    getHolder(m) {
      let obj = {
          w: 0,
          h: 0,
          x: 0,
          y: 0
        },
        ratio = 16 / 9;
      if (m.w / m.h > ratio) {
        this.moreRaio(obj, m, ratio);
      } else {
        this.lessRatio(obj, m, ratio);
      }
      return obj;
    },
    getRatioMatrix(m) {
      let obj = {
        w: 0,
        h: 0,
        x: 0,
        y: 0
      };
      this.ratioN = this.ratio > 0 ? this.ratio : this.initSize.r;
      if (this.ratioN > 16 / 9) {
        this.lessRatio(obj, m, this.ratioN);
      } else {
        this.moreRaio(obj, m, this.ratioN);
      }
      return obj;
    },
    getVideoMatrix(m) {
      let obj = {
          w: 0,
          h: 0,
          x: 0,
          y: 0
        },
        ratio = this.initSize.r;
      if (ratio < this.ratioN) {
        this.moreRaio(obj, m, ratio);
      } else {
        this.lessRatio(obj, m, ratio);
      }
      return obj;
    },
    lessRatio(obj, m, ratio) {
      obj.w = m.w;
      obj.h = m.w / ratio;
      obj.x = 0;
      obj.y = (m.h - obj.h) / 2;
    },
    moreRaio(obj, m, ratio) {
      obj.h = m.h;
      obj.w = m.h * ratio;
      obj.y = 0;
      obj.x = (m.w - obj.w) / 2;
    },
    resizeMatrix(obj, zoom) {
      obj.w *= zoom;
      obj.h *= zoom;
      obj.x *= zoom;
      obj.y *= zoom;
    },
    dragDown(dis, action) {
      if (!action) return;
      let m = this.handleSize;
      let min = 0,
        max = 0,
        min1 = 0,
        max1 = 0,
        cli = 12;
      switch (action) {
        case 'n':
          // 上
          min = 0;
          max = m.y + m.h - cli;
          break;
        case 'e':
          // 右
          min = cli;
          max = this.ratioM.w - m.x;
          break;
        case 's':
          // 下
          min = cli;
          max = this.ratioM.h - m.y;
          break;
        case 'w':
          // 左
          min = 0;
          max = m.x + m.w - cli;
          break;
        case 'sw':
          // 左下
          min = cli;
          max = this.ratioM.h - m.y;
          min1 = 0;
          max1 = m.x + m.w - cli;
          break;
        case 'nw':
          // 左上
          min = 0;
          max = m.y + m.h - cli;
          min1 = 0;
          max1 = m.x + m.w - cli;
          break;
        case 'ne':
          // 右上
          min = 0;
          max = m.y + m.h - cli;
          min1 = cli;
          max1 = this.ratioM.w - m.x;
          break;
        case 'se':
          // 右下
          min = cli;
          max = this.ratioM.h - m.y;
          min1 = cli;
          max1 = this.ratioM.w - m.x;
          break;
        case 'move':
          m = this.videoSize;
          break;
      }
      this.action = action;
      this.mpos = {
        disx: dis.x,
        disy: dis.y,
        w: m.w,
        h: m.h,
        x: m.x,
        y: m.y,
        min: min,
        max: max,
        min1: min1,
        max1: max1,
        adsorbArr: [
          [this.handleSize.y, this.handleSize.y + this.handleSize.h],
          [this.handleSize.x + this.handleSize.w, this.handleSize.x]
        ]
      };
    },
    dragMove(pos) {
      if (!this.action) return;
      let mpos = this.mpos;
      let m = { w: mpos.w, h: mpos.h, x: mpos.x, y: mpos.y };
      switch (this.action) {
        case 'n':
          // 上
          m.y = this.getChangeVal(pos.y, mpos.disy, mpos.y, mpos.min, mpos.max);
          m.h = mpos.y - m.y + mpos.h;
          break;
        case 'e':
          m.w = this.getChangeVal(pos.x, mpos.disx, mpos.w, mpos.min, mpos.max);
          // 右
          break;
        case 's':
          // 下
          m.h = this.getChangeVal(pos.y, mpos.disy, mpos.h, mpos.min, mpos.max);
          break;
        case 'w':
          // 左
          m.x = this.getChangeVal(pos.x, mpos.disx, mpos.x, mpos.min, mpos.max);
          m.w = mpos.x - m.x + mpos.w;
          break;
        case 'sw':
          // 左下
          m.h = this.getChangeVal(pos.y, mpos.disy, mpos.h, mpos.min, mpos.max);
          m.x = this.getChangeVal(pos.x, mpos.disx, mpos.x, mpos.min1, mpos.max1);
          m.w = mpos.x - m.x + mpos.w;
          break;
        case 'nw':
          // 左上
          m.y = this.getChangeVal(pos.y, mpos.disy, mpos.y, mpos.min, mpos.max);
          m.h = mpos.y - m.y + mpos.h;
          m.x = this.getChangeVal(pos.x, mpos.disx, mpos.x, mpos.min1, mpos.max1);
          m.w = mpos.x - m.x + mpos.w;
          break;
        case 'ne':
          // 右上
          m.y = this.getChangeVal(pos.y, mpos.disy, mpos.y, mpos.min, mpos.max);
          m.h = mpos.y - m.y + mpos.h;
          m.w = this.getChangeVal(pos.x, mpos.disx, mpos.w, mpos.min1, mpos.max1);
          break;
        case 'se':
          // 右下
          m.h = this.getChangeVal(pos.y, mpos.disy, mpos.h, mpos.min, mpos.max);
          m.w = this.getChangeVal(pos.x, mpos.disx, mpos.w, mpos.min1, mpos.max1);
          break;
        case 'move':
          m.x = pos.x - mpos.disx + mpos.x;
          m.y = pos.y - mpos.disy + mpos.y;
          // 吸附（case中不能使用adsorb）
          var adsorb = [m.y, m.x + m.w, m.y + m.h, m.x],
            arr = mpos.adsorbArr,
            data = [{}, {}, {}, {}];
          for (let j in adsorb) {
            let index = j % 2;
            for (let k in arr[index]) {
              if (Math.abs(adsorb[j] - arr[index][k]) <= 5) {
                data[j].k = adsorb[j];
                data[j].v = arr[index][k];
                data[j].arr = adsorb;
              }
            }
          }
          data.forEach((v, i) => {
            if (typeof v.v === 'number') {
              if (i % 2 === 0) {
                m.y = v.v - v.arr[i] + m.y;
              } else {
                m.x = v.v - v.arr[i] + m.x;
              }
            }
          });
          Object.assign(this.videoSize, m);
          return;
      }

      Object.assign(this.handleSize, m);
    },
    dragUp() {
      if (!this.action) return;
      this.action = '';
      this.mpos = null;
    },
    getChangeVal(n, p, v, min, max) {
      let num = n - p + v;
      if (num < min) {
        num = min;
      } else if (num > max) {
        num = max;
      }
      return num;
    },
    mousewheel(e) {
      e.preventDefault();
      if (e.ctrlKey) {
        let x = e.pageX - this.matrix.x - this.holder.x - this.ratioM.x;
        let y = e.pageY - this.matrix.y + this.holder.y + this.ratioM.y;
        let scale = this.wheelScale;
        this.wheelScale = this.wheelScale + (e.wheelDelta > 0 ? 0.05 : -0.05);
        if (this.wheelScale >= 5) {
          this.wheelScale = 5;
        } else if (this.wheelScale <= 0.05) {
          this.wheelScale = 0.05;
        }
        scale = this.wheelScale / scale;
        this.scaleVideo(x, y, scale);
      }
    },
    scaleVideo(x, y, scale) {
      let m = this.videoSize;
      let dx = x - m.x;
      let dy = y - m.y;
      m.x = x - dx * scale;
      m.y = y - dy * scale;
      m.w *= scale;
      m.h *= scale;
    },
    getPostInfo() {
      let obj = {};
      if (this.ratioN > this.initSize.r) {
        obj.cHeight = this.initSize.h;
        obj.cWidth = obj.cHeight * this.ratioN;
      } else {
        obj.cWidth = this.initSize.w;
        obj.cHeight = obj.cWidth / this.ratioN;
      }
      let scale = this.ratioM.w / obj.cWidth;
      obj.zoom = this.videoSize.w / scale / this.initSize.w;
      obj.width = this.handleSize.w / scale;
      obj.height = this.handleSize.h / scale;
      obj.cPointX = this.handleSize.x / scale;
      obj.cPointY = this.handleSize.y / scale;
      obj.pointX = (this.handleSize.x - this.videoSize.x) / scale;
      obj.pointY = (this.handleSize.y - this.videoSize.y) / scale;
      obj.bgcolor = this.bgColor;
      if (this.rotate === 90 || this.rotate === 270) {
        obj.sWidth = this.initSize.h;
        obj.sHeight = this.initSize.w;
      } else {
        obj.sWidth = this.initSize.w;
        obj.sHeight = this.initSize.h;
      }

      return obj;
    },
    selectColor() {
      this.$bus.emit('popup_show', {
        type: 'selectColor',
        info: {
          x: this.matrix.x + this.holder.x + this.ratioM.x + this.ratioM.w + 10,
          y: this.matrix.y + this.holder.y + this.ratioM.y,
          color: this.bgColor,
          callback: (color) => {
            color && (this.bgColor = color);
          }
        }
      });
    }
  }
};
</script>
<style lang="scss">
.canvas-editor {
  position: relative;
  width: 100%;
  height: 100%;
}
.editor-holder {
  position: absolute;
}
.editor-ratio {
  position: absolute;
  overflow: hidden;
  background-color: #000000;
}
.editor-video {
  position: absolute;
  overflow: hidden;

  > div {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: center;
  }

  video {
    width: 100%;
    height: 100%;
  }
}
.editor-handle {
  position: absolute;
  border: 2px solid #2f58fe;
  box-shadow: 0 0 0 1999px rgba(0, 0, 0, 0.5);
}
.handle-view-box {
  display: block;
  height: 100%;
  width: 100%;
  overflow: hidden;

  > span {
    display: block;
    position: absolute;
    border: 0 solid #8ba2ff;
    opacity: 0.4;
  }

  .handle-line-h {
    border-bottom-width: 2px;
    border-top-width: 2px;
    height: 33.33333%;
    left: 0;
    top: 33.33333%;
    width: 100%;
  }
  .handle-line-v {
    border-left-width: 2px;
    border-right-width: 2px;
    height: 100%;
    left: 33.33333%;
    top: 0;
    width: 33.33333%;
  }
}
.handle-drag-box {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  > span {
    display: block;
    height: 100%;
    opacity: 0;
    position: absolute;
    width: 100%;
  }

  .line-e {
    width: 4px;
    right: -4px;
    top: 0;
    cursor: ew-resize;
  }
  .line-n {
    height: 4px;
    left: 0;
    top: -3px;
    cursor: ns-resize;
  }
  .line-w {
    width: 4px;
    left: -3px;
    top: 0;
    cursor: ew-resize;
  }
  .line-s {
    height: 4px;
    bottom: -3px;
    left: 0;
    cursor: ns-resize;
  }
  .canvas-point {
    width: 26px;
    height: 26px;
    border: 4px solid #2f58fe;
  }
  .point-ne {
    opacity: 1;
    right: -2px;
    top: -2px;
    border-left: 0;
    border-bottom: 0;
    cursor: nesw-resize;
  }
  .point-nw {
    opacity: 1;
    left: -2px;
    top: -2px;
    border-right: 0;
    border-bottom: 0;
    cursor: nwse-resize;
  }
  .point-sw {
    opacity: 1;
    bottom: -2px;
    left: -2px;
    border-right: 0;
    border-top: 0;
    cursor: nesw-resize;
  }
  .point-se {
    opacity: 1;
    bottom: -2px;
    right: -2px;
    border-left: 0;
    border-top: 0;
    cursor: nwse-resize;
  }
}
.color-pick-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  background: #ffffff;
  border-radius: 50%;
  cursor: pointer;

  .hover {
    display: none;
  }

  &:hover {
    .normal {
      display: none;
    }
    .hover {
      display: block;
    }
  }
}
</style>
